import { usePageAccessChecker } from 'src/app/AppAuth/usePageAccess'
import React, { useEffect, useRef, useState } from 'react'
import ResizeObserver from 'resize-observer-polyfill'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import { useReadAccessChecker } from 'src/app/AppAuth/useReadAccess'
import { GridProps, TypographyProps } from 'styled-system'
import { InfoButton } from 'src/components/PageLayout/InfoButton'
import BootstrapTab from 'react-bootstrap/Tab'
import { TabContainerProps } from 'react-bootstrap/TabContainer'
import { CountChip } from '../Chips/BasicChip'
import {
  Tooltip,
  TooltipOverlayType,
  TooltipPositions,
} from '../Tooltip/Tooltip'
import { TableListLoader } from '../ContentLoaders/TableListLoader'
import {
  TabContainer,
  TabRow,
  TabContent,
  TabNav,
  TabNavScroller,
  TabNote,
  TabScrollButton,
  TabTitle,
  TabNavItem,
} from './Tabs.styles'

export type TabType = {
  requiredPageCapability?: CAPABILITY
  requiredCapabilities?: CAPABILITY[]
  title: string
  toolTipBody?: string
  count?: number | JSX.Element
  body: JSX.Element
  note?: JSX.Element
  disabled?: boolean
  tabKey?: string | number
  unmountOnExit?: boolean
  // Delay mounting the pane until it's first activated.
  mountOnEnter?: boolean
}

// Whether tabs sit hidden past either edge of the scrolling strip. Re-read on
// scroll and on any resize of the strip or its tabs (counts load in late and
// widen them), keyed on the tab count so new tabs are observed too.
const useTabStripOverflow = (
  navRef: React.RefObject<HTMLElement>,
  tabCount: number,
): { left: boolean; right: boolean } => {
  const [overflow, setOverflow] = useState({ left: false, right: false })
  useEffect(() => {
    const nav = navRef.current
    if (!nav) return
    const update = () => {
      const left = nav.scrollLeft > 0
      // 1px of slack: scroll positions are fractional at some zoom levels.
      const right = nav.scrollLeft + nav.clientWidth < nav.scrollWidth - 1
      setOverflow((prev) =>
        prev.left === left && prev.right === right ? prev : { left, right },
      )
    }
    update()
    const observer = new ResizeObserver(update)
    observer.observe(nav)
    Array.from(nav.children).forEach((tab) => observer.observe(tab))
    nav.addEventListener('scroll', update, { passive: true })
    return () => {
      observer.disconnect()
      nav.removeEventListener('scroll', update)
    }
  }, [navRef, tabCount])
  return overflow
}

type TabsProps = {
  tabs: TabType[]
  defaultActiveKey?: string | number
  activeKey?: string | number
  onSelect?: TabContainerProps['onSelect']
  tabTitleFontStyles?: TypographyProps
  gridStyles?: GridProps
  fillHeight?: boolean
  // Drops the strip's side inset, for tabs inside a card.
  flush?: boolean
  // Bordered tabs sitting on the pane's top border; the active one opens
  // into it. The pane draws its own border.
  attached?: boolean
}

export function Tabs({
  tabs,
  defaultActiveKey = '0',
  onSelect,
  tabTitleFontStyles,
  gridStyles,
  activeKey,
  fillHeight,
  flush,
  attached,
}: TabsProps): JSX.Element {
  const canRead = useReadAccessChecker()
  const canOpenPage = usePageAccessChecker()
  const [selectedKey, setSelectedKey] = useState(defaultActiveKey)
  // Assign fallback keys before filtering so URLs and callers using numeric
  // keys continue to identify the same pane when a related feature is hidden.
  const visibleTabs = tabs
    .map((tab, idx) => ({ ...tab, tabKey: tab.tabKey ?? idx }))
    .filter(
      (tab) =>
        (!tab.requiredPageCapability ||
          canOpenPage(tab.requiredPageCapability)) &&
        (tab.requiredCapabilities || []).every(canRead),
    )
  const requestedKey = activeKey ?? selectedKey
  const resolvedKey = visibleTabs.some(
    (tab) => String(tab.tabKey) === String(requestedKey),
  )
    ? requestedKey
    : visibleTabs[0]?.tabKey
  const navRef = useRef<HTMLElement>(null)
  const overflow = useTabStripOverflow(navRef, visibleTabs.length)
  // A deep-linked tab past the edge would otherwise be selected but unseen.
  // Scrolls the strip itself sideways rather than calling scrollIntoView, which
  // also scrolls every scrolling ancestor vertically: a page stacking several
  // tab sets (the vendor guidance cards) would load scrolled down to the last.
  useEffect(() => {
    const nav = navRef.current
    const active = nav?.querySelector('.nav-link.active')
    if (!nav || !active) return
    const navBox = nav.getBoundingClientRect()
    const activeBox = active.getBoundingClientRect()
    if (activeBox.left < navBox.left) {
      nav.scrollLeft -= navBox.left - activeBox.left
    } else if (activeBox.right > navBox.right) {
      nav.scrollLeft += activeBox.right - navBox.right
    }
  }, [resolvedKey])
  const scrollTabs = (direction: -1 | 1) =>
    navRef.current?.scrollBy({
      left: direction * navRef.current.clientWidth * 0.8,
      behavior: 'smooth',
    })
  const innerTabs: JSX.Element[] = []
  const Panes: JSX.Element[] = []

  visibleTabs.forEach(
    (
      {
        title,
        toolTipBody,
        count,
        body,
        note,
        disabled,
        tabKey,
        unmountOnExit,
        mountOnEnter,
      },
      idx,
    ) => {
      const eventKey = tabKey !== undefined ? tabKey : idx
      innerTabs.push(
        <TabNavItem key={`${title}-${eventKey}`}>
          <TabTitle
            eventKey={eventKey}
            {...tabTitleFontStyles}
            data-testid={`TAB_IDX_${eventKey}`}
            disabled={disabled}
            $attached={attached}
          >
            <span className="tab-title">{title}</span>
            {count !== undefined && <CountChip ml="xs">{count}</CountChip>}
            {toolTipBody && (
              <Tooltip
                id={`tab-tooltip-${title}`}
                placement={TooltipPositions.right}
                overlayType={TooltipOverlayType.popover}
                tooltipBody={toolTipBody}
              >
                <InfoButton as="div" ml="xxs" />
              </Tooltip>
            )}
          </TabTitle>
        </TabNavItem>,
      )

      Panes.push(
        <BootstrapTab.Pane
          key={eventKey}
          eventKey={eventKey}
          unmountOnExit={unmountOnExit}
          mountOnEnter={mountOnEnter}
        >
          {note && <TabNote>{note}</TabNote>}
          {body}
        </BootstrapTab.Pane>,
      )
    },
  )

  if (!visibleTabs.length) return <></>
  return (
    <BootstrapTab.Container
      activeKey={resolvedKey}
      onSelect={(key, event) => {
        if (key !== null) setSelectedKey(key)
        onSelect?.(key, event)
      }}
    >
      <TabContainer {...gridStyles} $fillHeight={fillHeight}>
        <TabRow $fillHeight={fillHeight}>
          <TabNavScroller $attached={attached}>
            {overflow.left && (
              <TabScrollButton
                type="button"
                $side="left"
                aria-label="Scroll tabs left"
                onClick={() => scrollTabs(-1)}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </TabScrollButton>
            )}
            <TabNav ref={navRef} $flush={flush} $attached={attached}>
              {innerTabs}
            </TabNav>
            {overflow.right && (
              <TabScrollButton
                type="button"
                $side="right"
                aria-label="Scroll tabs right"
                onClick={() => scrollTabs(1)}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </TabScrollButton>
            )}
          </TabNavScroller>
        </TabRow>
        <TabRow $fillHeight={fillHeight} $content $attached={attached}>
          <TabContent $fillHeight={fillHeight}>{Panes}</TabContent>
        </TabRow>
      </TabContainer>
    </BootstrapTab.Container>
  )
}

export const getLoadingTab = ({ title }: { title: string }): TabType => ({
  title,
  count: 0,
  body: <TableListLoader />,
})
